// /account/security — manage two-factor authentication.
// Per mfa-enrollment-spec.md §6. Authored with keyed i18n strings; re-skinned
// onto the design-system in Phase G L13 (logic + i18n keys unchanged).
//
// Sub-views, driven by `mode`:
//   view       — MFA status + actions (enable, or disable/regenerate)
//   enroll     — enrollment wizard: intro → scan-and-verify
//   codes      — show one-time backup codes (after enroll-verify or regenerate)
//   disable    — confirm-disable form (password + code)
//   regenerate — regenerate-backup-codes form (code)

// Leaf subpath import — never the @vysto/shared barrel or ./auth (jsonwebtoken).
import { PASSWORD_RULES, passwordMeetsPolicy } from '@vysto/shared/auth/password-policy'
import { type FormEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { AccountTabsHeader } from './account/AccountTabsHeader.js'
import {
  getMfaStatus,
  postChangePassword,
  postMfaDisable,
  postMfaEnrollStart,
  postMfaEnrollVerify,
  postMfaRegenerateBackupCodes,
  type MfaEnrollStart,
  type MfaStatus,
} from '../api/client.js'
import { describeError } from '../api/describe-error.js'
import { useAuthStore } from '../auth/store.js'
import { Alert, Button, Card, FormField, Input, PasswordInput } from '../design-system/index.js'

/** Map the 4-rule policy onto the PasswordInput meter's 3 levels (same mapping
 *  as the reset screen). */
function strengthLevel(pw: string): 'weak' | 'mid' | 'strong' {
  const met = PASSWORD_RULES.filter((r) => r.test(pw)).length
  return met <= 1 ? 'weak' : met === 2 ? 'mid' : 'strong'
}

type Mode = 'view' | 'enroll' | 'codes' | 'disable' | 'regenerate'

const CODE_INPUT = 'text-center font-mono tracking-widest'

export function AccountSecurityPage(): JSX.Element {
  const { t } = useTranslation('common')
  const [searchParams] = useSearchParams()
  // The interceptor appends ?politica=mfa when the team policy bounced the
  // user here — explain it instead of leaving a silent redirect.
  const bouncedByPolicy = searchParams.get('politica') === 'mfa'

  const [status, setStatus] = useState<MfaStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)

  const [mode, setMode] = useState<Mode>('view')
  const [enrollStep, setEnrollStep] = useState<'intro' | 'scan'>('intro')
  const [enrollData, setEnrollData] = useState<MfaEnrollStart | null>(null)
  const [backupCodes, setBackupCodes] = useState<readonly string[]>([])

  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  async function reloadStatus(): Promise<void> {
    setStatus(await getMfaStatus())
  }

  useEffect(() => {
    void (async () => {
      try {
        await reloadStatus()
      } catch {
        setLoadFailed(true)
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  function toView(): void {
    setMode('view')
    setEnrollStep('intro')
    setEnrollData(null)
    setBackupCodes([])
    setCode('')
    setPassword('')
    setFormError(null)
  }

  function go(next: Mode): void {
    setCode('')
    setPassword('')
    setFormError(null)
    setMode(next)
  }

  async function handleStart(): Promise<void> {
    setBusy(true)
    setFormError(null)
    try {
      setEnrollData(await postMfaEnrollStart())
      setEnrollStep('scan')
    } catch (err) {
      setFormError(describeError(err))
    } finally {
      setBusy(false)
    }
  }

  async function handleVerify(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault()
    setBusy(true)
    setFormError(null)
    try {
      const result = await postMfaEnrollVerify(code.trim())
      // Apply the fresh mfa_verified session on the spot — under the
      // obligatoria policy this is what un-bounces the console without the
      // logout/login dance (checkpoint-2 finding). Guarded on the tokens
      // actually being present: during a deploy the web can briefly run ahead
      // of the api, and setSession(undefined) corrupts the store (the exact
      // failure the #713 PR e2e caught against the pre-#712 api).
      const user = useAuthStore.getState().user
      if (user !== null && result.access_token && result.refresh_token) {
        useAuthStore.getState().setSession({
          accessToken: result.access_token,
          refreshToken: result.refresh_token,
          user,
        })
      }
      setBackupCodes(result.backup_codes)
      setMode('codes')
    } catch (err) {
      setFormError(describeError(err))
    } finally {
      setBusy(false)
    }
  }

  async function handleDisable(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault()
    setBusy(true)
    setFormError(null)
    try {
      await postMfaDisable(password, code.trim(), false)
      await reloadStatus()
      toView()
    } catch (err) {
      setFormError(describeError(err))
    } finally {
      setBusy(false)
    }
  }

  async function handleRegenerate(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault()
    setBusy(true)
    setFormError(null)
    try {
      setBackupCodes(await postMfaRegenerateBackupCodes(code.trim()))
      setMode('codes')
    } catch (err) {
      setFormError(describeError(err))
    } finally {
      setBusy(false)
    }
  }

  async function handleCodesDone(): Promise<void> {
    setBusy(true)
    try {
      await reloadStatus()
    } catch {
      /* status refresh is best-effort; the codes were already shown */
    } finally {
      setBusy(false)
      toView()
    }
  }

  const sixDigits = /^\d{6}$/.test(code)

  return (
    <div data-testid="account-security" className="mx-auto w-full max-w-4xl">
      <AccountTabsHeader active="seguridad" />

      <Card className="mt-5 w-full max-w-lg">
        <Card.Body>
          <h1 className="text-[17px] font-bold tracking-[-0.01em] text-text">
            {t('account.security.title')}
          </h1>
          <h2 className="mt-1 text-[12.5px] font-medium text-text-2">
            {t('account.security.mfa.heading')}
          </h2>

          {bouncedByPolicy && (
            <Alert tone="warn" className="mt-4">
              {t('account.security.mfa.policy_bounce')}
            </Alert>
          )}

          {formError !== null && (
            <Alert tone="sens" className="mt-4">
              {formError}
            </Alert>
          )}

          <div className="mt-4">
            {loading && (
              <p className="text-[12.5px] text-text-2">{t('account.security.loading')}</p>
            )}

            {!loading && loadFailed && (
              <Alert tone="sens">{t('account.security.load_error')}</Alert>
            )}

            {!loading && !loadFailed && status !== null && (
              <>
                {mode === 'view' && (
                  <ViewSection
                    t={t}
                    status={status}
                    onEnable={() => go('enroll')}
                    onDisable={() => go('disable')}
                    onRegenerate={() => go('regenerate')}
                  />
                )}

                {mode === 'enroll' && enrollStep === 'intro' && (
                  <div className="flex flex-col gap-4">
                    <p className="text-[12.5px] text-text-2">
                      {t('account.security.mfa.wizard.intro')}
                    </p>
                    <Button block disabled={busy} onClick={() => void handleStart()}>
                      {t('account.security.mfa.wizard.intro_cta')}
                    </Button>
                    <Button variant="secondary" block onClick={toView} disabled={busy}>
                      {t('account.security.mfa.disable.cancel_cta')}
                    </Button>
                  </div>
                )}

                {mode === 'enroll' && enrollStep === 'scan' && enrollData !== null && (
                  <form
                    onSubmit={(e) => void handleVerify(e)}
                    className="flex flex-col gap-4"
                    noValidate
                  >
                    <img
                      src={enrollData.qr_code_data_url}
                      alt={t('account.security.mfa.wizard.qr_alt')}
                      className="mx-auto size-44 rounded-md border border-line"
                    />
                    <div>
                      <p className="text-[11px] text-text-3">
                        {t('account.security.mfa.wizard.manual_secret_label')}
                      </p>
                      <code
                        data-testid="manual-secret"
                        className="mt-1 block break-all rounded bg-field px-2 py-1 font-mono text-[11px] text-text"
                      >
                        {enrollData.manual_secret}
                      </code>
                    </div>
                    <FormField
                      label={t('account.security.mfa.wizard.verify_label')}
                      htmlFor="enroll-code"
                    >
                      <Input
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        disabled={busy}
                        placeholder="123456"
                        className={CODE_INPUT}
                      />
                    </FormField>
                    <Button type="submit" block disabled={busy || !sixDigits}>
                      {t('account.security.mfa.wizard.verify_cta')}
                    </Button>
                  </form>
                )}

                {mode === 'codes' && (
                  <div className="flex flex-col gap-4">
                    <h3 className="text-[12.5px] font-semibold text-text">
                      {t('account.security.mfa.wizard.backup_codes_title')}
                    </h3>
                    <Alert tone="warn">
                      {t('account.security.mfa.wizard.backup_codes_warning')}
                    </Alert>
                    <ul
                      data-testid="backup-codes"
                      className="grid grid-cols-2 gap-2 rounded-md bg-field p-3 font-mono text-[12.5px] text-text"
                    >
                      {backupCodes.map((c) => (
                        <li key={c}>{c}</li>
                      ))}
                    </ul>
                    <Button block disabled={busy} onClick={() => void handleCodesDone()}>
                      {t('account.security.mfa.wizard.backup_codes_done_cta')}
                    </Button>
                  </div>
                )}

                {mode === 'disable' && (
                  <form
                    onSubmit={(e) => void handleDisable(e)}
                    className="flex flex-col gap-4"
                    noValidate
                  >
                    <h3 className="text-[12.5px] font-semibold text-text">
                      {t('account.security.mfa.disable.heading')}
                    </h3>
                    <FormField
                      label={t('account.security.mfa.disable.password_label')}
                      htmlFor="disable-password"
                    >
                      {/* Same show/hide eye as every other password field —
                          checkpoint-1 follow-up: this one was missed. */}
                      <PasswordInput
                        id="disable-password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={busy}
                      />
                    </FormField>
                    <FormField
                      label={t('account.security.mfa.disable.code_label')}
                      htmlFor="disable-code"
                    >
                      <Input
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        disabled={busy}
                        placeholder="123456"
                        className={CODE_INPUT}
                      />
                    </FormField>
                    <Button
                      type="submit"
                      block
                      disabled={busy || password.length === 0 || !sixDigits}
                    >
                      {t('account.security.mfa.disable.confirm_cta')}
                    </Button>
                    <Button variant="secondary" block onClick={toView} disabled={busy}>
                      {t('account.security.mfa.disable.cancel_cta')}
                    </Button>
                  </form>
                )}

                {mode === 'regenerate' && (
                  <form
                    onSubmit={(e) => void handleRegenerate(e)}
                    className="flex flex-col gap-4"
                    noValidate
                  >
                    <h3 className="text-[12.5px] font-semibold text-text">
                      {t('account.security.mfa.regenerate.heading')}
                    </h3>
                    <p className="text-[12.5px] text-text-2">
                      {t('account.security.mfa.regenerate.intro')}
                    </p>
                    <FormField
                      label={t('account.security.mfa.regenerate.code_label')}
                      htmlFor="regen-code"
                    >
                      <Input
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        disabled={busy}
                        placeholder="123456"
                        className={CODE_INPUT}
                      />
                    </FormField>
                    <Button type="submit" block disabled={busy || !sixDigits}>
                      {t('account.security.mfa.regenerate.confirm_cta')}
                    </Button>
                    <Button variant="secondary" block onClick={toView} disabled={busy}>
                      {t('account.security.mfa.regenerate.cancel_cta')}
                    </Button>
                  </form>
                )}
              </>
            )}
          </div>
        </Card.Body>
      </Card>

      <PasswordCard t={t} />
    </div>
  )
}

function PasswordCard({ t }: { t: ReturnType<typeof useTranslation>['t'] }): JSX.Element {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const policyOk = passwordMeetsPolicy(next)
  const matches = next.length > 0 && next === confirm
  const canSubmit = current.length > 0 && policyOk && matches && !busy

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault()
    setBusy(true)
    setError(null)
    setDone(false)
    try {
      await postChangePassword(current, next)
      setDone(true)
      setCurrent('')
      setNext('')
      setConfirm('')
    } catch (err) {
      setError(describeError(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card className="mt-5 w-full max-w-lg" data-testid="password-card">
      <Card.Body>
        <h2 className="text-[12.5px] font-medium text-text-2">
          {t('account.security.password.heading')}
        </h2>
        <p className="mt-1 text-[12.5px] text-text-2">{t('account.security.password.intro')}</p>

        {error !== null && (
          <Alert tone="sens" className="mt-4">
            {error}
          </Alert>
        )}
        {done && (
          <Alert tone="ok" className="mt-4">
            {t('account.security.password.success')}
          </Alert>
        )}

        <form onSubmit={(e) => void onSubmit(e)} className="mt-4 flex flex-col gap-4" noValidate>
          {/* PasswordInput = the login/reset field with the show/hide eye —
              checkpoint-1 finding: hidden-only fields here made typos
              undiagnosable. The new-password one also brings the rules
              checklist + strength meter, same as the reset screen. */}
          <FormField label={t('account.security.password.current_label')} htmlFor="pw-current">
            <PasswordInput
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              disabled={busy}
            />
          </FormField>
          <FormField label={t('account.security.password.new_label')} htmlFor="pw-new">
            <PasswordInput
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              disabled={busy}
              rules={PASSWORD_RULES.map((r) => ({ label: r.label, met: r.test(next) }))}
              {...(next.length > 0 ? { strength: strengthLevel(next) } : {})}
            />
          </FormField>
          <FormField label={t('account.security.password.confirm_label')} htmlFor="pw-confirm">
            <PasswordInput
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              disabled={busy}
            />
          </FormField>
          {confirm.length > 0 && !matches && (
            <p className="text-[11px] text-sens">{t('account.security.password.mismatch')}</p>
          )}
          <Button type="submit" block disabled={!canSubmit}>
            {t('account.security.password.change_cta')}
          </Button>
        </form>
      </Card.Body>
    </Card>
  )
}

function ViewSection({
  t,
  status,
  onEnable,
  onDisable,
  onRegenerate,
}: {
  t: ReturnType<typeof useTranslation>['t']
  status: MfaStatus
  onEnable: () => void
  onDisable: () => void
  onRegenerate: () => void
}): JSX.Element {
  if (!status.mfa_enabled) {
    return (
      <div className="flex flex-col gap-4">
        <StatusRow t={t} enabled={false} />
        <p className="text-[12.5px] text-text-2">{t('account.security.mfa.intro_disabled')}</p>
        <Button block onClick={onEnable}>
          {t('account.security.mfa.enable_cta')}
        </Button>
      </div>
    )
  }
  return (
    <div className="flex flex-col gap-4">
      <StatusRow t={t} enabled={true} />
      <p className="text-[12.5px] text-text-2">
        {t('account.security.mfa.backup_codes_remaining', { count: status.backup_codes_remaining })}
      </p>
      <Button block onClick={onRegenerate}>
        {t('account.security.mfa.regenerate_cta')}
      </Button>
      {status.mfa_mandatory ? (
        <p className="text-[11px] text-text-3">{t('account.security.mfa.mandatory_note')}</p>
      ) : (
        <Button variant="secondary" block onClick={onDisable}>
          {t('account.security.mfa.disable_cta')}
        </Button>
      )}
    </div>
  )
}

function StatusRow({
  t,
  enabled,
}: {
  t: ReturnType<typeof useTranslation>['t']
  enabled: boolean
}): JSX.Element {
  return (
    <div className="flex items-center gap-2 text-[12.5px]">
      <span className="text-text-2">{t('account.security.mfa.status_label')}:</span>
      <span
        data-testid="mfa-status"
        className={
          enabled
            ? 'rounded-pill bg-ok-bg px-2 py-0.5 text-[11px] font-semibold text-ok'
            : 'rounded-pill bg-field px-2 py-0.5 text-[11px] font-semibold text-text-2'
        }
      >
        {enabled
          ? t('account.security.mfa.status.enabled')
          : t('account.security.mfa.status.disabled')}
      </span>
    </div>
  )
}
